import { ProductType } from './product.types'

export type ProductSettings = Partial<Record<ProductSetting | ProductSettingAlias | ProductSettingOptions, unknown>>

export interface ProductSettingOption {
  key: ProductSetting
  label: string
  type: ProductSettingValue
  min?: number
  description: string
  default?: string | number | boolean
  updateTooltip?: string // tooltip displayed when the setting is updated
}

export interface ProductSettingsOptions {
  [key: string]: ProductSettingOption[]
}

export enum ProductSettingAlias {
  ORDER = 'alias_order',
  SUMMARY = 'alias_summary',
  PRINT_TICKET_PRIMARY = 'alias_print_ticket_primary',
  PRINT_TICKET_SECONDARY = 'alias_print_ticket_secondary'
}

export enum ProductSettingOptions {
  FARE = 'option_fare',
  ROUTE = 'option_route',
  OPTION = 'option_option',
  MODIFIER = 'option_modifier',
  RESTRICTIONS = 'option_restrictions'
}

export enum ProductSetting {
  AVAILABLE_ONLINE = 'setting_available_online',
  MINIMUM = 'setting_minimum',
  DEPARTURE_PORT = 'setting_departure_port',
  AVAILABLE_USAGES = 'setting_available_usages',
  AVAILABLE_IN_POS = 'setting_available_in_pos',
  AVAILABLE_AT_MY_ACCOUNT = 'setting_available_at_my_account',
  PRINT_RECEIPT = 'setting_print_receipt',
  PRINT_TEMPLATE = 'setting_print_template',
  VALIDITY = 'setting_validity',
  SPECIAL_CONDITION = 'setting_special_condition',
  ASSIGNABLE_TO_CARD = 'setting_assignable_to_card',
  DEPARTURE_RESTRICTION = 'setting_departure_restriction',
  ROUND_TRIP_RESTRICTION = 'setting_round_trip_restriction',
  AVAILABLE_FOR_TOP_UP = 'setting_available_for_top_up',

  /**
   * The (1-based) index of the page where this product should appear in the POS grid.
   */
  POS_POSITION_PAGE = 'setting_pos_position_page',
  POS_POSITION_ROW = 'setting_pos_position_row',
  POS_POSITION_COLUMN = 'setting_pos_position_column',
  POS_COLOR_BACKGROUND = 'setting_pos_color_background',
  POS_COLOR_FOREGROUND = 'setting_pos_color_foreground',
  POS_MINIMUM_SALE_QUANTITY = 'setting_pos_minimum_sale_quantity',
  POS_MAXIMUM_SALE_QUANTITY = 'setting_pos_maximum_sale_quantity'
}

/**
 * Product value setting interface
 */
export enum ProductSettingValue {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  LOCATION = 'location',
  PRINTER_TEMPLATE = 'printer_template',
  HEX_COLOR = 'hex_color'
}

export const SETTINGS: Record<ProductSetting, ProductSettingOption> = {
  [ProductSetting.AVAILABLE_ONLINE]: {
    key: ProductSetting.AVAILABLE_ONLINE,
    label: 'Available online',
    type: ProductSettingValue.BOOLEAN,
    description: 'Whether the product is available for online purchase'
  },
  [ProductSetting.MINIMUM]: {
    key: ProductSetting.MINIMUM,
    label: 'Minimum quantity',
    type: ProductSettingValue.NUMBER,
    description: 'The minimum quantity of the product that can be purchased'
  },
  [ProductSetting.DEPARTURE_PORT]: {
    key: ProductSetting.DEPARTURE_PORT,
    label: 'Departure port',
    type: ProductSettingValue.LOCATION,
    description: 'The port where the ticket is valid'
  },
  [ProductSetting.AVAILABLE_USAGES]: {
    key: ProductSetting.AVAILABLE_USAGES,
    label: 'Number of uses',
    type: ProductSettingValue.NUMBER,
    min: 1,
    description: 'Number of times the ticket can be used (1 for single trip, 2 for round trip)'
  },
  [ProductSetting.AVAILABLE_IN_POS]: {
    key: ProductSetting.AVAILABLE_IN_POS,
    label: 'Available in POS',
    type: ProductSettingValue.BOOLEAN,
    description: 'Whether the product is available for point of sale purchase'
  },
  [ProductSetting.AVAILABLE_AT_MY_ACCOUNT]: {
    key: ProductSetting.AVAILABLE_AT_MY_ACCOUNT,
    label: 'Available at my account',
    type: ProductSettingValue.BOOLEAN,
    description: 'Whether the product is available for purchase at my account',
    default: true
  },
  [ProductSetting.PRINT_RECEIPT]: {
    key: ProductSetting.PRINT_RECEIPT,
    label: 'Print receipt',
    type: ProductSettingValue.BOOLEAN,
    description: 'Triggers the receipt printing'
  },
  [ProductSetting.PRINT_TEMPLATE]: {
    key: ProductSetting.PRINT_TEMPLATE,
    label: 'Print template',
    type: ProductSettingValue.PRINTER_TEMPLATE,
    description: 'The template to use for the ticket printing'
  },
  [ProductSetting.VALIDITY]: {
    key: ProductSetting.VALIDITY,
    label: 'Validity (days) from date of purchase',
    type: ProductSettingValue.NUMBER,
    min: 1,
    description: 'Prompts the user to set the expiration date'
  },
  [ProductSetting.SPECIAL_CONDITION]: {
    key: ProductSetting.SPECIAL_CONDITION,
    label: 'Special condition',
    type: ProductSettingValue.STRING,
    description: 'Will be added in the ticket payload'
  },
  [ProductSetting.ASSIGNABLE_TO_CARD]: {
    key: ProductSetting.ASSIGNABLE_TO_CARD,
    label: 'Card assignable',
    type: ProductSettingValue.BOOLEAN,
    description: 'Triggers the card dialog'
  },
  [ProductSetting.DEPARTURE_RESTRICTION]: {
    key: ProductSetting.DEPARTURE_RESTRICTION,
    label: 'Departure restriction',
    type: ProductSettingValue.LOCATION,
    description: 'Saves a port departure restriction. Null means no port departure restriction'
  },
  [ProductSetting.ROUND_TRIP_RESTRICTION]: {
    key: ProductSetting.ROUND_TRIP_RESTRICTION,
    label: 'Direction of travel enforced',
    type: ProductSettingValue.BOOLEAN,
    description: 'Whether the ticket must be used only one time, each way'
  },
  [ProductSetting.POS_POSITION_PAGE]: {
    key: ProductSetting.POS_POSITION_PAGE,
    label: 'POS page',
    type: ProductSettingValue.NUMBER,
    min: 1,
    description: 'The page of the product in the POS grid'
  },
  [ProductSetting.POS_POSITION_ROW]: {
    key: ProductSetting.POS_POSITION_ROW,
    label: 'POS page row',
    type: ProductSettingValue.NUMBER,
    min: 1,
    description: 'The row of the product in the POS grid'
  },
  [ProductSetting.POS_POSITION_COLUMN]: {
    key: ProductSetting.POS_POSITION_COLUMN,
    label: 'POS page column',
    type: ProductSettingValue.NUMBER,
    min: 1,
    description: 'The column of the product in the POS grid'
  },
  [ProductSetting.POS_COLOR_BACKGROUND]: {
    key: ProductSetting.POS_COLOR_BACKGROUND,
    label: 'POS background color',
    type: ProductSettingValue.HEX_COLOR,
    description: 'The background color of the product in the POS grid'
  },
  [ProductSetting.POS_COLOR_FOREGROUND]: {
    key: ProductSetting.POS_COLOR_FOREGROUND,
    label: 'POS foreground color',
    type: ProductSettingValue.HEX_COLOR,
    description: 'The foreground color of the product in the POS grid'
  },
  [ProductSetting.POS_MINIMUM_SALE_QUANTITY]: {
    key: ProductSetting.POS_MINIMUM_SALE_QUANTITY,
    label: 'POS minimum sale quantity',
    type: ProductSettingValue.NUMBER,
    min: 1,
    description: 'The minimum quantity of the product that can be sold in the POS'
  },
  [ProductSetting.POS_MAXIMUM_SALE_QUANTITY]: {
    key: ProductSetting.POS_MAXIMUM_SALE_QUANTITY,
    label: 'POS maximum sale quantity',
    type: ProductSettingValue.NUMBER,
    min: 1,
    description: 'The maximum quantity of the product that can be sold in the POS',
    updateTooltip: 'By changing the number, no changes are applied to existing orders'
  },
  [ProductSetting.AVAILABLE_FOR_TOP_UP]: {
    key: ProductSetting.AVAILABLE_FOR_TOP_UP,
    label: 'Top-up eligible',
    type: ProductSettingValue.BOOLEAN,
    description: 'Whether the product is available for top-up',
    default: true
  }
}

export const BASE_SETTINGS: ProductSettingOption[] = [
  SETTINGS[ProductSetting.AVAILABLE_ONLINE],
  SETTINGS[ProductSetting.AVAILABLE_IN_POS],
  SETTINGS[ProductSetting.POS_COLOR_BACKGROUND],
  SETTINGS[ProductSetting.POS_COLOR_FOREGROUND],
  SETTINGS[ProductSetting.POS_MINIMUM_SALE_QUANTITY],
  SETTINGS[ProductSetting.POS_MAXIMUM_SALE_QUANTITY],
  SETTINGS[ProductSetting.POS_POSITION_PAGE],
  SETTINGS[ProductSetting.POS_POSITION_COLUMN],
  SETTINGS[ProductSetting.POS_POSITION_ROW]
]

// Some settings are commented out because their functionality is not implemented yet
export const SETTINGS_OPTIONS: ProductSettingsOptions = {
  [ProductType.TICKET]: [
    ...BASE_SETTINGS,
    SETTINGS[ProductSetting.AVAILABLE_AT_MY_ACCOUNT],
    SETTINGS[ProductSetting.AVAILABLE_USAGES],
    SETTINGS[ProductSetting.PRINT_TEMPLATE],
    SETTINGS[ProductSetting.VALIDITY],
    SETTINGS[ProductSetting.SPECIAL_CONDITION],
    SETTINGS[ProductSetting.ASSIGNABLE_TO_CARD],
    SETTINGS[ProductSetting.ROUND_TRIP_RESTRICTION],
    SETTINGS[ProductSetting.AVAILABLE_FOR_TOP_UP]
  ]
}

/**
 * Prefixes for the settings keys
 */
export enum SETTINGS_PREFIX {
  SETTING = 'setting_',
  OPTION = 'option_',
  ALIAS = 'alias_'
}
